<script setup lang="ts">
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Checkbox from "primevue/checkbox";
import Listbox from "primevue/listbox";
import Sidebar from "primevue/sidebar";
import { computed, ref } from "vue";
import FilterPillBar from "@/components/OperationsApp/components/FilterPillBar/index.vue";
import { useFilters } from "@/components/OperationsApp/composables/useFilters.ts";
import { useProductTypeReasonFilters } from "@/components/OperationsApp/composables/useProductTypeReasonFilters.ts";
import { useSearchSessions } from "@/components/OperationsApp/composables/useSearchSessions.ts";
import { useUpdateDecisionDates } from "@/components/OperationsApp/composables/useUpdateDecisionDates.ts";
import { useGetCitiesData } from "@/providers/cities/useGetCitiesData.ts";
import { ORDER_STATUSES_OPTIONS, PRODUCT_TYPE_OPTIONS } from "./constants.ts";
import SortBy from "./SortBy.vue";
import { filtersPresets } from "./styles.ts";
import type { Props } from "./types.ts";

const props = withDefaults(defineProps<Props>(), {});

const visibleRight = ref(false);
const isExpanded = ref(false);

const { orderStatus, updateOrderStatus, city, updateCity, session, updateSession, status, clearFilters } =
  useFilters();

const { dates, updateDecisionDates } = useUpdateDecisionDates();

const { data: inUseCities, isLoading: isLoadingInUseCities } = useGetCitiesData({
  limit: 1000,
  page: 1,
  in_use: true,
});

const {
  isLoadingOriginalSessionsOptions,
  isLoadingSearchSessionsOptions,
  sessionOptions,
  debouncedSessionNameUpdate,
  sessionLookupName,
} = useSearchSessions(status, session);

const {
  libraryId,
  reasonId,
  isReasonEnabled,
  overrideReasonsData,
  updateReasonId,
  updateLibraryId,
  isProductTypeEnabled,
} = useProductTypeReasonFilters();

const closeSidebar = () => {
  visibleRight.value = false;
};

const clear = async () => {
  await clearFilters();
  closeSidebar();
};

const updateCityCheckBox = (val) => {
  const foundIndex = city.value?.indexOf(val);
  if (foundIndex >= 0) {
    city.value.splice(foundIndex, 1);
  } else {
    city.value.push(val);
  }
  updateCity(city.value);
};
</script>

<template>
  <div class="flex mt-4">
    <Button @click="visibleRight = true" @mouseover="isExpanded = true" @mouseleave="isExpanded = false" outlined class="!rounded-full !text-cyan-200 !border-cyan-200 h-8 min-w-12 bg-white">
      <div class="flex align-center justify-center items-center">
        <span class="material-symbols-outlined">tune</span>
        <span class="ml-2 leading-8" v-show="isExpanded">Filters</span>
      </div>
    </Button>
    <FilterPillBar />
    <Sidebar v-model:visible="visibleRight" header="Right Sidebar" position="right" :pt="filtersPresets.sidebar" >
    <template #header>
      <div class="flex justify-between items-center">
        <span class="material-symbols-outlined">tune</span>
        <div class="font-bold uppercase ml-2">Price Override Filters</div>
      </div>
    </template>
    <Accordion :activeIndex="0" :multiple="true" class="shadow-2xl p-6 h-full overflow-y-scroll" :pt="filtersPresets.accordion">
      <AccordionTab header="Program Session" :pt="filtersPresets.accordion">
        <div>
          <Listbox
            :model-value="session"
            @update:model-value="value => updateSession"
            :options="sessionOptions"
            filter
            multiple
            optionValue="id"
            optionLabel="name"
            class="w-full md:w-14rem !border-none mb-4"
            listStyle="max-height:250px"
            :pt="filtersPresets.listbox"
            filterPlaceholder="Search and Select"
            @filter="debouncedSessionNameUpdate"
            :selectionMessage="sessionLookupName"
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <Checkbox v-model="session" @update:model-value="updateSession" :inputId="`${slotProps.option.id}`" name="session" :value="slotProps.option.id" :pt="filtersPresets.checkbox"/>
                <label :for="`${slotProps.option.id}`" class="ml-4 text-wrap">{{ slotProps.option.name }}</label>
              </div>
            </template>
          </Listbox>
        </div>
      </AccordionTab>
      <AccordionTab header="City" :pt="filtersPresets.accordion">
        <div v-if="!isLoadingInUseCities && inUseCities && inUseCities?.length > 0">
          <Listbox
            v-model="city"
            :options="inUseCities"
            filter
            multiple
            optionValue="id"
            optionLabel="city_ascii"
            class="w-full md:w-14rem !border-none mb-4"
            listStyle="max-height:250px"
            :pt="filtersPresets.listbox"
            filterPlaceholder="Search and Select"
          >
            <template #option="slotProps">
              <div class="flex align-items-center" @click="updateCityCheckBox(slotProps.option.id)">
                <Checkbox v-model="city" :inputId="`${slotProps.option.id}`" name="city" :value="slotProps.option.id" :pt="filtersPresets.checkbox"/>
                <label :for="`${slotProps.option.id}`" class="ml-4">{{ slotProps.option.city_ascii }}</label>
              </div>
            </template>
          </Listbox>
        </div>
      </AccordionTab>
      <AccordionTab header="Product Type" :pt="filtersPresets.accordion">
        <div v-for="productTypeOption of PRODUCT_TYPE_OPTIONS" :key="productTypeOption.key" class="flex items-center mb-4 ml-5">
          <Checkbox :disabled="!isProductTypeEnabled(productTypeOption.key)" v-model="libraryId" @update:model-value="updateLibraryId" :inputId="productTypeOption.key" name="productType" :value="productTypeOption.key" :pt="filtersPresets.checkbox"/>
          <label :for="productTypeOption.key" :class="['ml-4', {'!opacity-40 ': !isProductTypeEnabled(productTypeOption.key) }]">{{ productTypeOption.name }}</label>
        </div>
      </AccordionTab>
      <AccordionTab header="Reason Type" :pt="filtersPresets.accordion">
        <div v-for="overrideReasonsOption of overrideReasonsData?.overrideReasonsOptions" :key="overrideReasonsOption.id" class="flex items-center mb-4 ml-5">
          <Checkbox :disabled="!isReasonEnabled(overrideReasonsOption.id)" v-model="reasonId" @update:model-value="updateReasonId" :inputId="overrideReasonsOption.id" name="reasonType" :value="overrideReasonsOption.id" :pt="filtersPresets.checkbox"/>
          <label :for="overrideReasonsOption.id" :class="['ml-4', {'!opacity-40 ': !isReasonEnabled(overrideReasonsOption.id) }]">{{ overrideReasonsOption.name }}</label>
        </div>
      </AccordionTab>
      <AccordionTab header="Decisions Date" :pt="filtersPresets.accordion">
        <Calendar
          class="mb-4 ml-2"
          :model-value="dates"
          selectionMode="range"
          :manualInput="false"
          showIcon
          :pt="filtersPresets.calendar"
          @update:model-value="updateDecisionDates"
        />
      </AccordionTab>
      <AccordionTab header="Order Status" :pt="filtersPresets.accordion">
        <div v-for="orderStatusOption of ORDER_STATUSES_OPTIONS" :key="orderStatusOption.key" class="flex items-center mb-4 ml-5">
          <Checkbox v-model="orderStatus" @update:model-value="updateOrderStatus" :inputId="orderStatusOption.key" name="orderStatus" :value="orderStatusOption.key" :pt="filtersPresets.checkbox"/>
          <label :for="orderStatusOption.key" class="ml-4">{{ orderStatusOption.name }}</label>
        </div>
      </AccordionTab>
    </Accordion>
    <div class="flex justify-between m-4">
      <Button icon="pi pi-check" severity="secondary" label="Clear" class="ais-ClearRefinements rounded-none bg-transparent border-0 text-teal-900 p-4" @click="clear" :pt="filtersPresets.button"/>
      <Button icon="pi pi-check" label="Close" class="rounded-none bg-teal-900 text-white p-4" @click="closeSidebar" :pt="filtersPresets.button"/>
    </div>
  </Sidebar>
    <SortBy class="ml-auto"/>
  </div>
</template>

<style scoped>
.top-shadow {
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.1);
}
</style>
